// ModuleView — extracted from App.js (Feb 2026 refactor).
// Behavior-preserving 1:1 copy. Rendered inside a Layout received as prop.

import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Award, CheckCircle, Loader2 } from "lucide-react";

import { Button } from "../components/UIPrimitives";
import PDLLearningWrapper from "../components/PDLLearningWrapper";
import AccessibilityBar from "../components/AccessibilityBar";
import ModuleChecklist from "../components/ModuleChecklist";
import LernpaketMiniCTA from "../components/LernpaketMiniCTA";
import { MiniPoster } from "../components/GalleryWall";
import QuizViewPage from "./QuizView";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const KAPITEL_WORTE = ['', 'EINS', 'ZWEI', 'DREI', 'VIER', 'FÜNF', 'SECHS', 'SIEBEN', 'ACHT', 'NEUN', 'ZEHN', 'ELF', 'ZWÖLF', 'DREIZEHN', 'VIERZEHN', 'FÜNFZEHN', 'SECHZEHN', 'SIEBZEHN', 'ACHTZEHN', 'NEUNZEHN', 'ZWANZIG', 'EINUNDZWANZIG', 'ZWEIUNDZWANZIG', 'DREIUNDZWANZIG', 'VIERUNDZWANZIG', 'FÜNFUNDZWANZIG', 'SECHSUNDZWANZIG', 'SIEBENUNDZWANZIG', 'ACHTUNDZWANZIG', 'NEUNUNDZWANZIG', 'DREISSIG', 'EINUNDDREISSIG', 'ZWEIUNDDREISSIG', 'DREIUNDDREISSIG', 'VIERUNDDREISSIG', 'FÜNFUNDDREISSIG', 'SECHSUNDDREISSIG', 'SIEBENUNDDREISSIG', 'ACHTUNDDREISSIG', 'NEUNUNDDREISSIG', 'VIERZIG'];
const kapitelWort = (n) => KAPITEL_WORTE[n] || String(n);
const stripModulPrefix = (t) => (t || '').replace(/^modul\s*\d+\s*[:\-–—]\s*/i, '').trim();

const ModuleView = ({ Layout }) => {
  const { moduleId } = useParams();
  const { getAuthHeader } = useAuth();
  const [module, setModule] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [nextModule, setNextModule] = useState(null);
  const readBarRef = useRef(null);
  const [showRitual, setShowRitual] = useState(false);
  useEffect(() => {
    if (module?.title) document.title = `${stripModulPrefix(module.title)} · Dickies-Campus`;
  }, [module]);
  const navigate = useNavigate();
  const timeTrackingRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      const pct = total > 0 ? Math.min(100, ((window.scrollY || el.scrollTop) / total) * 100) : 0;
      if (readBarRef.current) readBarRef.current.style.width = `${pct}%`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [moduleId]);

  useEffect(() => {
    loadModule();
    
    // Start time tracking
    startTimeRef.current = Date.now();
    
    // Track time every 30 seconds
    timeTrackingRef.current = setInterval(() => {
      trackTime();
    }, 30000);
    
    // Track time when leaving the page
    return () => {
      if (timeTrackingRef.current) {
        clearInterval(timeTrackingRef.current);
      }
      trackTime();
    };
  }, [moduleId]);

  const trackTime = async () => {
    if (!startTimeRef.current) return;
    
    const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
    startTimeRef.current = Date.now(); // Reset for next interval
    
    if (timeSpent > 0 && timeSpent < 120) { // Max 2 minutes per interval to avoid fake data
      try {
        await axios.post(`${API}/tracking/time`, {
          module_id: moduleId,
          time_spent_seconds: timeSpent
        }, getAuthHeader());
      } catch (error) {
        // Silent fail for tracking
      }
    }
  };

  const loadModule = async () => {
    try {
      const response = await axios.get(`${API}/modules/${moduleId}`, getAuthHeader());
      setModule(response.data);
      // Fetch parent course to know shop_url + title for the cross-sell CTA
      const courseId = response.data?.course_id;
      if (courseId) {
        try {
          const cRes = await axios.get(`${API}/courses/${courseId}`);
          setCourse(cRes.data);
        } catch { /* silent — CTA falls back to shop overview */ }
        try {
          const mRes = await axios.get(`${API}/courses/${courseId}/modules`, getAuthHeader());
          const list = mRes.data || [];
          const idx = list.findIndex((m) => m.id === moduleId);
          setNextModule(idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null);
        } catch { /* silent */ }
      }
    } catch (error) {
      toast.error("Fehler beim Laden des Moduls");
    }
    setLoading(false);
  };

  const markComplete = async () => {
    try {
      await axios.post(`${API}/modules/${moduleId}/complete`, {}, getAuthHeader());
      setShowRitual(true);
      setTimeout(() => setShowRitual(false), 2600);
      loadModule();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-terra-dark" />
        </div>
      </Layout>
    );
  }

  if (showQuiz) {
    return <QuizViewPage moduleId={moduleId} onBack={() => setShowQuiz(false)} Layout={Layout} />;
  }

  if (!module) {
    return (
      <Layout>
        <div className="text-center py-20" data-testid="module-not-available">
          <p className="font-display text-2xl text-espresso">Dieses Kapitel ist für dich noch nicht freigeschaltet.</p>
          <p className="text-sm text-espresso/60 mt-3">Bitte wende dich an deine Kursleitung, falls du Zugriff erwartest.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout moduleId={moduleId} moduleTitle={module?.title}>
      {/* Scrollgebundene Lesefortschritts-Linie */}
      <div className="dh-reading-progress" aria-hidden>
        <div ref={readBarRef} style={{ width: '0%' }} />
      </div>

      {/* Kapitel-Abschluss-Ritual */}
      {showRitual && (
        <div className="dh-ritual-overlay" data-testid="chapter-ritual">
          <p className="font-display text-3xl md:text-[40px] text-center px-6" style={{ color: "#F5F0EA", lineHeight: 1.2 }}>
            Kapitel {module?.order || ''} — <span className="italic" style={{ color: "#E7CFA2" }}>gelesen</span>.
          </p>
          <div className="mt-6" style={{ width: 60, height: 1, backgroundColor: "#D89232" }} />
        </div>
      )}

      <div className="max-w-5xl mx-auto animate-fade-in">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/course/${module?.course_id}`)}
          className="flex items-center gap-2 text-sand-700 hover:text-terra-dark mb-6 transition-colors"
          data-testid="back-to-course"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zum Kurs
        </button>

        {/* Artikel-Kopf */}
        {module && (
          <div className="mb-8" data-testid="article-head">
            <p className="text-[11px] uppercase font-bold text-terra mb-2" style={{ letterSpacing: "0.3em" }}>
              Kapitel {kapitelWort(module.order)}{(module.ue_count || module.ue) ? ` · ${module.ue_count || module.ue} UE` : ''}
            </p>
            <h1 className="font-display text-3xl md:text-[38px] text-espresso" style={{ lineHeight: 1.15 }}>
              {stripModulPrefix(module.title)}
            </h1>
            <p className="font-display italic text-sand-600 mt-2 text-[15px]">
              ~{Math.max(10, Math.ceil((module.content?.length || 0) / 1200))} Minuten · nimm dir einen Kaffee dazu.
            </p>
            <ModuleChecklist
              module={module}
              getAuthHeader={getAuthHeader}
              refreshKey={`${showQuiz}-${module?.user_progress?.completed}`}
            />
          </div>
        )}

        {/* Barrierefreiheit: Vorlesen + Lernvideo (Pilotkurse) */}
        <AccessibilityBar module={module} authHeader={getAuthHeader()} onVideoQuizCompleted={loadModule} />

        {/* PDL Learning Wrapper with Timer and Tasks (for PDL courses) */}
        {/* Falls back to ModuleContentViewer for other courses */}
        <PDLLearningWrapper 
          module={module}
          courseId={module?.course_id}
          onBack={() => navigate(`/courses/${module?.course_id}`)}
          onStartQuiz={() => setShowQuiz(true)}
          getAuthHeader={getAuthHeader}
        />

        {/* Cross-sell: printed Lernpaket at end of module content */}
        <LernpaketMiniCTA shopUrl={course?.shop_url} courseTitle={course?.title} />

        {/* Galerie-Poster des Tages */}
        <MiniPoster seed={moduleId} className="mt-8" />

        {/* Progress Status Bar */}
        {module?.user_progress && (
          <div className="mt-8 py-4 px-1 border-t border-b" style={{ borderColor: "rgba(216,146,50,0.35)" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                {module.user_progress.completed && (
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold flex items-center gap-2" style={{ color: "#8F650F" }}>
                    <CheckCircle className="w-4 h-4" />
                    Kapitel gelesen
                  </span>
                )}
                {module.user_progress.quiz_passed && (
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold flex items-center gap-2" style={{ color: "#8F650F" }}>
                    <Award className="w-4 h-4" />
                    Quiz bestanden · {Math.round(module.user_progress.quiz_score)}%
                  </span>
                )}
              </div>
              {!module.user_progress.completed && (
                <Button onClick={markComplete} size="sm" data-testid="complete-module">
                  <CheckCircle className="w-4 h-4" />
                  Als gelesen markieren
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Umblättern — nächstes Kapitel */}
        {nextModule && (
          <div className="mt-10 pt-8 border-t border-cream-300 text-center pb-4" data-testid="next-chapter-link">
            <p className="font-mono text-[10px] uppercase font-semibold mb-3" style={{ letterSpacing: "0.25em", color: "#8F650F" }}>
              Umblättern
            </p>
            <button
              onClick={() => { navigate(`/module/${nextModule.id}`); window.scrollTo(0, 0); }}
              className="group font-display text-2xl md:text-[28px] text-espresso hover:text-terra transition-colors leading-snug"
            >
              Weiter mit Kapitel {nextModule.order}: <span className="italic text-terra">{stripModulPrefix(nextModule.title)}</span>
              <ArrowRight className="inline-block ml-2 h-5 w-5 -mt-1 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ModuleView;
